'use client';

import { ArrowLeft, Calendar, Check, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  AuthPageLogo,
  AuthPageWrapper,
} from '@/components/auth/auth-page-wrapper';
import { CountryCombobox } from '@/components/auth/country-combobox';
import { FormFieldWithError } from '@/components/auth/form-field-with-error';
import {
  GoogleLogoMark,
  OauthBrandIconSlot,
} from '@/components/auth/oauth-provider-marks';
import { useLocale } from '@/components/common/locale-provider';
import { PluginDownloadStep } from '@/components/downloads/plugin-download-step';
import { toast } from '@/components/shared/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { env } from '@/config/env';
import { useTranslations } from '@/hooks/use-translations';
import { captureEnterpriseBookingCtaClicked } from '@/lib/analytics/enterprise-booking';
import { trackGoogleTagManagerEvent } from '@/lib/analytics/google-tag-manager';
import {
  createMetaEventId,
  getMetaEventHeaders,
  META_COMPLETE_REGISTRATION_EVENT,
  trackMetaEvent,
} from '@/lib/analytics/meta';
import { useSession } from '@/lib/auth/auth-client';
import { countrySelectOptions } from '@/lib/auth/iso-countries';
import { getEnterpriseContactUrl } from '@/lib/auth/organization-capabilities';
import { getSignupErrorMessage } from '@/lib/i18n/signup-error-messages';
import type { TranslationKey } from '@/lib/i18n/utils';
import { cn } from '@/lib/utils';
import { validateEmail, validatePassword } from '@/lib/utils/validation';
import { EnterprisePassForm } from './enterprise-pass-form';

type FlowStep =
  | 'welcome'
  | 'password'
  | 'verify'
  | 'about'
  | 'plan'
  | 'enterprise'
  | 'download';

type UsageIntent = 'solo' | 'team';
type SignupPlanId = 'free' | 'pro' | 'enterprise';

interface PendingSignupState {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  authMethod: 'credential' | 'oauth';
  isEmailVerified: boolean;
  jurisdiction: string | null;
  companyRole: string | null;
  usageIntent: UsageIntent | null;
  selectedPlanId: SignupPlanId | null;
  status: 'draft' | 'enterprise_handoff' | 'checkout_pending' | 'completed';
  hasCompletedCheckout: boolean;
  nextStep: Exclude<FlowStep, 'download'>;
  verificationCodeSentAt: string | null;
}

interface SignupStateResponse {
  draft: PendingSignupState | null;
  nextStep: Exclude<FlowStep, 'download'>;
  enterpriseContactUrl: string;
  proCheckoutUrl: string;
}

function shouldForceOAuthReauth(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    new URLSearchParams(window.location.search).get('oauth_reauth') === '1'
  );
}

const COMPANY_ROLE_OPTIONS = [
  'Architect',
  'Engineer',
  'BIM Manager',
  'Project Manager',
  'Document Controller',
  'Compliance Manager',
  'Contractor',
  'Other',
] as const;

const SIGNUP_PLAN_CARD_OPTIONS: readonly {
  id: Exclude<SignupPlanId, 'free'>;
  titleKey: TranslationKey;
  taglineKey: TranslationKey;
  priceKey: TranslationKey;
  priceDetailKey: TranslationKey;
  featureKeys: readonly TranslationKey[];
}[] = [
  {
    id: 'pro',
    titleKey: 'auth.signupFlow.planProTitle',
    taglineKey: 'auth.signupFlow.planProDescription',
    priceKey: 'auth.signupFlow.planProPrice',
    priceDetailKey: 'auth.signupFlow.planProPriceDetail',
    featureKeys: [
      'auth.signupFlow.planProFeature1',
      'auth.signupFlow.planProFeature2',
      'auth.signupFlow.planProFeature3',
      'auth.signupFlow.planProFeature4',
      'auth.signupFlow.planProFeature5',
    ],
  },
];

const COMPANY_ROLE_LABEL_KEY: Record<
  (typeof COMPANY_ROLE_OPTIONS)[number],
  TranslationKey
> = {
  Architect: 'auth.signupFlow.roleArchitect',
  Engineer: 'auth.signupFlow.roleEngineer',
  'BIM Manager': 'auth.signupFlow.roleBimManager',
  'Project Manager': 'auth.signupFlow.roleProjectManager',
  'Document Controller': 'auth.signupFlow.roleDocumentController',
  'Compliance Manager': 'auth.signupFlow.roleComplianceManager',
  Contractor: 'auth.signupFlow.roleContractor',
  Other: 'auth.signupFlow.roleOther',
};

const EMAIL_VALIDATION_ERROR_KEYS: Record<string, TranslationKey> = {
  'Email is required': 'auth.signupFlow.validation.emailRequired',
  'Please enter a valid email address':
    'auth.signupFlow.validation.emailInvalid',
};

const PASSWORD_VALIDATION_ERROR_KEYS: Record<string, TranslationKey> = {
  'Password is required': 'auth.signupFlow.validation.passwordRequired',
  'Password must be at least 8 characters long':
    'auth.signupFlow.validation.passwordTooShort',
  'Password must contain at least one uppercase letter, one lowercase letter, and one number':
    'auth.signupFlow.validation.passwordComplexity',
};

function AuthDivider({ orLabel }: { orLabel: string }) {
  return (
    <div className="flex w-full items-center gap-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
      <div className="h-px flex-1 bg-border" />
      <span>{orLabel}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export function SignupPageContent({
  isEnterprisePass = false,
}: {
  isEnterprisePass?: boolean;
}) {
  const router = useRouter();
  const { locale } = useLocale();
  const { t } = useTranslations(locale);
  const tRef = useRef(t);
  tRef.current = t;
  const { data: session } = useSession();

  const [draft, setDraft] = useState<PendingSignupState | null>(null);
  const [step, setStep] = useState<FlowStep>('welcome');
  const [enterpriseContactUrl, setEnterpriseContactUrl] = useState(
    getEnterpriseContactUrl,
  );
  const [proCheckoutUrl, setProCheckoutUrl] = useState('/signup/checkout');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const usageIntent: UsageIntent = 'solo';
  const [companyRole, setCompanyRole] = useState('');
  const [customCompanyRole, setCustomCompanyRole] = useState('');
  const [planId, setPlanId] = useState<SignupPlanId>('pro');

  useEffect(() => {
    if (session?.user && !isEnterprisePass) {
      router.push('/');
    }
  }, [router, session, isEnterprisePass]);

  useEffect(() => {
    if (draft) {
      setFirstName(draft.firstName ?? '');
      setLastName(draft.lastName ?? '');
      setEmail(draft.email);
      setJurisdiction(draft.jurisdiction ?? '');
      const savedRole = draft.companyRole ?? '';
      if (
        savedRole &&
        COMPANY_ROLE_OPTIONS.some((option) => option === savedRole)
      ) {
        setCompanyRole(savedRole);
        setCustomCompanyRole('');
      } else if (savedRole) {
        setCompanyRole('Other');
        setCustomCompanyRole(savedRole);
      } else {
        setCompanyRole('');
        setCustomCompanyRole('');
      }
      setPlanId(draft.selectedPlanId === 'enterprise' ? 'enterprise' : 'pro');
    }
  }, [draft]);

  useEffect(() => {
    let cancelled = false;

    const loadState = async () => {
      try {
        const response = await fetch('/api/auth/signup/state', {
          credentials: 'include',
        });
        const data = (await response.json()) as SignupStateResponse;

        if (cancelled) {
          return;
        }

        setDraft(data.draft);
        const forceOAuthReauth = shouldForceOAuthReauth();

        if (forceOAuthReauth) {
          window.history.replaceState({}, '', '/signup');
        }
        setStep(
          data.draft
            ? forceOAuthReauth && data.draft.authMethod === 'oauth'
              ? 'welcome'
              : data.nextStep
            : 'welcome',
        );
        setEnterpriseContactUrl(data.enterpriseContactUrl);
        setProCheckoutUrl(data.proCheckoutUrl);
      } catch (_error) {
        toast({
          type: 'error',
          description: tRef.current('auth.signupFlow.toast.loadFailed'),
        });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadState();

    return () => {
      cancelled = true;
    };
  }, []);

  const canUseGoogle = Boolean(env.NEXT_PUBLIC_GOOGLE_CLIENT_ID);

  const verificationEmail = draft?.email ?? email.trim();

  const welcomeValidationError = useMemo(() => {
    if (!firstName.trim()) {
      return t('auth.signupFlow.validation.firstNameRequired');
    }
    if (!lastName.trim()) {
      return t('auth.signupFlow.validation.lastNameRequired');
    }
    const emailError = validateEmail(email.trim());
    if (!emailError) {
      return null;
    }
    const key = EMAIL_VALIDATION_ERROR_KEYS[emailError.message];
    return key ? t(key) : emailError.message;
  }, [email, firstName, lastName, t]);

  const passwordValidationError = useMemo(() => {
    const err = validatePassword(password);
    if (!err) {
      return null;
    }
    const key = PASSWORD_VALIDATION_ERROR_KEYS[err.message];
    return key ? t(key) : err.message;
  }, [password, t]);

  const canContinueAbout = Boolean(jurisdiction);

  const handleGoogleSignup = async () => {
    if (!canUseGoogle) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.googleNotConfigured'),
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/signup/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'self_serve', isEnterprisePass }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast({
          type: 'error',
          description: getSignupErrorMessage(
            t,
            data,
            'auth.signupFlow.toast.googleStartFailed',
          ),
        });
        return;
      }

      if (!data.authorizationUrl || typeof data.authorizationUrl !== 'string') {
        toast({
          type: 'error',
          description: t('auth.signupFlow.toast.googleStartFailed'),
        });
        return;
      }

      window.location.href = data.authorizationUrl;
    } catch (_error) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.googleStartFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIdentityContinue = async () => {
    if (welcomeValidationError) {
      toast({ type: 'error', description: welcomeValidationError });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/signup/state', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step: 'identity',
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errorCode === 'ACCOUNT_EXISTS') {
          router.push(
            isEnterprisePass
              ? '/login?callbackUrl=/signup/enterprise'
              : '/login?message=account-exists',
          );
          return;
        }

        if (data.errorCode === 'DOMAIN_CLAIMED') {
          const orgNames = (data.organizations ?? [])
            .map((o: { name: string }) => o.name)
            .join(', ');
          toast({
            type: 'error',
            description: t('auth.socialSSO.domainClaimed', {
              orgs: orgNames || t('auth.socialSSO.domainClaimedFallbackOrg'),
            }),
          });
          return;
        }

        toast({
          type: 'error',
          description: getSignupErrorMessage(
            t,
            data,
            'auth.signupFlow.toast.saveDetailsFailed',
          ),
        });
        return;
      }

      setDraft(data.draft);
      setStep(data.nextStep);
      setVerificationCode('');
    } catch (_error) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.saveDetailsFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWelcomeFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleIdentityContinue();
  };

  const handlePasswordFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handlePasswordContinue();
  };

  const handlePasswordContinue = async () => {
    if (passwordValidationError) {
      toast({ type: 'error', description: passwordValidationError });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/signup/state', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step: 'password',
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast({
          type: 'error',
          description: getSignupErrorMessage(
            t,
            data,
            'auth.signupFlow.toast.savePasswordFailed',
          ),
        });
        return;
      }

      setDraft(data.draft);
      setStep(data.nextStep);
      setVerificationCode('');
      toast({
        type: 'success',
        description: t('auth.signupFlow.toast.verificationSent', {
          email: verificationEmail,
        }),
      });
    } catch (_error) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.savePasswordFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.enterSixDigitCode'),
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/signup/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'verify',
          otp: verificationCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast({
          type: 'error',
          description: getSignupErrorMessage(
            t,
            data,
            'auth.signupFlow.toast.verifyEmailFailed',
          ),
        });
        return;
      }

      setDraft(data.draft);
      setStep(data.nextStep);
      setVerificationCode('');
      toast({
        type: 'success',
        description: t('auth.signupFlow.toast.emailVerified'),
      });
    } catch (_error) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.verifyEmailFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleVerifyCode();
  };

  const handleResendCode = async () => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/signup/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'resend' }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast({
          type: 'error',
          description: getSignupErrorMessage(
            t,
            data,
            'auth.signupFlow.toast.resendFailed',
          ),
        });
        return;
      }

      setDraft(data.draft);
      toast({
        type: 'success',
        description: t('auth.signupFlow.toast.resendSent', {
          email: verificationEmail,
        }),
      });
    } catch (_error) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.resendFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAboutContinue = async () => {
    if (!jurisdiction) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.aboutIncomplete'),
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/signup/state', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step: 'about',
          usageIntent,
          jurisdiction,
          companyRole:
            companyRole === 'Other' ? customCompanyRole.trim() : companyRole,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast({
          type: 'error',
          description: getSignupErrorMessage(
            t,
            data,
            'auth.signupFlow.toast.profileSaveFailed',
          ),
        });
        return;
      }

      setDraft(data.draft);
      setStep(data.nextStep);
    } catch (_error) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.profileSaveFailed'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const completeSignup = async () => {
    const completeRegistrationEventId = createMetaEventId();
    const response = await fetch('/api/auth/signup/complete', {
      method: 'POST',
      headers: getMetaEventHeaders({
        'x-meta-complete-registration-event-id': completeRegistrationEventId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      if (data.errorCode === 'ACCOUNT_EXISTS') {
        router.push(
          isEnterprisePass
            ? '/login?callbackUrl=/signup/enterprise'
            : '/login?message=account-exists',
        );
        return false;
      }

      if (data.errorCode === 'DOMAIN_CLAIMED') {
        const orgNames = (data.organizations ?? [])
          .map((o: { name: string }) => o.name)
          .join(', ');
        toast({
          type: 'error',
          description: t('auth.socialSSO.domainClaimed', {
            orgs: orgNames || t('auth.socialSSO.domainClaimedFallbackOrg'),
          }),
        });
        return false;
      }

      if (data.errorCode === 'OAUTH_REAUTH_REQUIRED') {
        setStep('welcome');
        toast({
          type: 'error',
          description: getSignupErrorMessage(
            t,
            data,
            'auth.signupFlow.toast.completeFailed',
          ),
        });
        return false;
      }

      toast({
        type: 'error',
        description: getSignupErrorMessage(
          t,
          data,
          'auth.signupFlow.toast.completeFailed',
        ),
      });
      return false;
    }

    if (data.accountCreated) {
      trackMetaEvent(
        'CompleteRegistration',
        META_COMPLETE_REGISTRATION_EVENT,
        completeRegistrationEventId,
      );
      if (data.googleTagManagerEvent) {
        trackGoogleTagManagerEvent(data.googleTagManagerEvent);
      }
    }
    setStep('download');
    return true;
  };

  const handlePlanContinue = async () => {
    if (!planId) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.choosePlan'),
      });
      return;
    }

    const selectedPlanId = planId;
    let isRedirectingToCheckout = false;
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/signup/state', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step: 'plan',
          planId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast({
          type: 'error',
          description: getSignupErrorMessage(
            t,
            data,
            'auth.signupFlow.toast.planSaveFailed',
          ),
        });
        return;
      }

      setDraft(data.draft);
      const savedPlanId = data.draft?.selectedPlanId ?? selectedPlanId;

      if (savedPlanId === 'enterprise') {
        setStep('enterprise');
        return;
      }

      if (savedPlanId === 'pro') {
        isRedirectingToCheckout = true;
        router.push(proCheckoutUrl);
        return;
      }

      await completeSignup();
    } catch (_error) {
      toast({
        type: 'error',
        description: t('auth.signupFlow.toast.planSaveFailed'),
      });
    } finally {
      if (!isRedirectingToCheckout) {
        setIsSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    setVerificationCode('');

    switch (step) {
      case 'password':
        setStep('welcome');
        break;
      case 'verify':
        setStep('password');
        break;
      case 'about':
        setStep(draft?.authMethod === 'oauth' ? 'welcome' : 'verify');
        break;
      case 'plan':
        setStep('about');
        break;
      case 'enterprise':
        setStep(planId === 'enterprise' ? 'plan' : 'about');
        break;
      default:
        setStep('welcome');
        break;
    }
  };

  const handleContinueToApp = () => {
    window.location.href = '/';
  };

  const handleEnterpriseContact = () => {
    captureEnterpriseBookingCtaClicked({
      source: 'signup_onboarding',
      organizationType: 'personal',
      organizationPlanId:
        draft?.selectedPlanId && draft.selectedPlanId !== 'enterprise'
          ? draft.selectedPlanId
          : undefined,
      userRole: 'pending_signup',
      contactUrl: enterpriseContactUrl,
    });
    window.open(
      enterpriseContactUrl,
      enterpriseContactUrl.startsWith('mailto:') ? '_self' : '_blank',
      enterpriseContactUrl.startsWith('mailto:')
        ? undefined
        : 'noopener,noreferrer',
    );
  };

  if (
    isEnterprisePass &&
    step !== 'download' &&
    (session?.user || step === 'plan' || step === 'enterprise')
  ) {
    return (
      <AuthPageWrapper maxWidth="max-w-md">
        <AuthPageLogo title={t('auth.signUp')} />
        <EnterprisePassForm
          isSignedIn={Boolean(session?.user)}
          onPrepared={session?.user ? undefined : completeSignup}
        />
      </AuthPageWrapper>
    );
  }

  if (isLoading) {
    return (
      <AuthPageWrapper maxWidth="max-w-md">
        <div className="flex min-h-[420px] items-center justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      </AuthPageWrapper>
    );
  }

  if (step === 'download') {
    return (
      <AuthPageWrapper maxWidth="max-w-md">
        <AuthPageLogo title={t('auth.signUp')} />
        <div className="flex min-h-[420px] w-full flex-col">
          <PluginDownloadStep onContinue={handleContinueToApp} />
        </div>
      </AuthPageWrapper>
    );
  }

  return (
    <AuthPageWrapper maxWidth="max-w-md">
      <AuthPageLogo title={t('auth.signUp')} />
      <div className="flex min-h-[420px] w-full flex-col gap-8">
        {step !== 'welcome' ? (
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex cursor-pointer items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            {t('common.back')}
          </button>
        ) : null}

        {isEnterprisePass && step === 'welcome' && (
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold">
              {t('enterprisePass.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('enterprisePass.description')}
            </p>
          </div>
        )}
        {step === 'welcome' ? (
          <div className="flex flex-col gap-6">
            {canUseGoogle ? (
              <div className="grid gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 justify-start"
                  onClick={() => void handleGoogleSignup()}
                  disabled={isSubmitting}
                >
                  <OauthBrandIconSlot>
                    <GoogleLogoMark />
                  </OauthBrandIconSlot>
                  {t('auth.signupFlow.continueGoogle')}
                </Button>
              </div>
            ) : null}

            {canUseGoogle ? <AuthDivider orLabel={t('common.or')} /> : null}

            <form
              className="flex flex-col gap-6"
              onSubmit={handleWelcomeFormSubmit}
              noValidate
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <FormFieldWithError
                  id="firstName"
                  label={t('common.firstName')}
                  type="text"
                  value={firstName}
                  onChange={setFirstName}
                  placeholder={t('auth.signupFlow.placeholderFirstName')}
                />
                <FormFieldWithError
                  id="lastName"
                  label={t('common.lastName')}
                  type="text"
                  value={lastName}
                  onChange={setLastName}
                  placeholder={t('auth.signupFlow.placeholderLastName')}
                />
              </div>

              <FormFieldWithError
                id="email"
                label={t('common.emailAddress')}
                type="email"
                value={email}
                onChange={setEmail}
                placeholder={t('common.emailPlaceholder')}
                disabled={draft?.authMethod === 'oauth'}
              />

              <Button type="submit" className="h-11" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : null}
                {t('auth.signupFlow.continue')}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link
                href={
                  isEnterprisePass
                    ? '/login?callbackUrl=/signup/enterprise'
                    : '/login'
                }
                className="cursor-pointer text-foreground hover:underline"
              >
                {t('auth.signIn')}
              </Link>
            </p>
          </div>
        ) : null}

        {step === 'password' ? (
          <div className="flex flex-col gap-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold">
                {t('auth.signupFlow.createPasswordTitle')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('auth.signupFlow.createPasswordDescription')}
              </p>
            </div>

            <form
              className="flex flex-col gap-6"
              onSubmit={handlePasswordFormSubmit}
              noValidate
            >
              <FormFieldWithError
                id="password"
                label={t('auth.password')}
                type="password"
                value={password}
                onChange={setPassword}
                placeholder={t('auth.signupFlow.passwordPlaceholder')}
                error={
                  password ? (passwordValidationError ?? undefined) : undefined
                }
              />

              <Button type="submit" className="h-11" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : null}
                {t('auth.signupFlow.continue')}
              </Button>
            </form>
          </div>
        ) : null}

        {step === 'verify' ? (
          <div className="flex flex-col gap-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold">
                {t('auth.signupFlow.verifyEmailTitle')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('auth.signupFlow.verifyEmailDescription', {
                  email: verificationEmail,
                })}
              </p>
            </div>

            <form
              className="flex flex-col gap-6"
              onSubmit={handleVerifyFormSubmit}
              noValidate
            >
              <div className="w-full">
                <InputOTP
                  maxLength={6}
                  value={verificationCode}
                  onChange={setVerificationCode}
                  containerClassName="w-full"
                  onPaste={(event) => {
                    const pastedData = event.clipboardData.getData('text');
                    const cleanedData = pastedData
                      .replace(/\D/g, '')
                      .slice(0, 6);
                    if (cleanedData) {
                      event.preventDefault();
                      setVerificationCode(cleanedData);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  <InputOTPGroup className="grid flex-1 grid-cols-3">
                    <InputOTPSlot index={0} className="h-11 w-full" />
                    <InputOTPSlot index={1} className="h-11 w-full" />
                    <InputOTPSlot index={2} className="h-11 w-full" />
                  </InputOTPGroup>
                  <InputOTPSeparator className="px-2" />
                  <InputOTPGroup className="grid flex-1 grid-cols-3">
                    <InputOTPSlot index={3} className="h-11 w-full" />
                    <InputOTPSlot index={4} className="h-11 w-full" />
                    <InputOTPSlot index={5} className="h-11 w-full" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                type="submit"
                className="h-11 w-full"
                disabled={isSubmitting || verificationCode.length !== 6}
              >
                {isSubmitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : null}
                {t('auth.signupFlow.verifyEmailButton')}
              </Button>
            </form>

            <Button
              type="button"
              variant="ghost"
              onClick={handleResendCode}
              disabled={isSubmitting}
            >
              {t('auth.signupFlow.resendCode')}
            </Button>
          </div>
        ) : null}

        {step === 'about' ? (
          <div className="flex flex-col gap-6">
            <h2 className="text-2xl font-semibold">
              {t('auth.signupFlow.aboutTitle')}
            </h2>

            <div className="space-y-2">
              <Label htmlFor="jurisdiction">
                {t('auth.signupFlow.countryLabel')}
              </Label>
              <CountryCombobox
                id="jurisdiction"
                value={jurisdiction}
                onValueChange={setJurisdiction}
                options={countrySelectOptions}
              />
            </div>

            <div className="space-y-3">
              <Label>{t('auth.signupFlow.roleQuestion')}</Label>

              <div className="flex flex-wrap gap-2.5">
                {COMPANY_ROLE_OPTIONS.map((option) => {
                  const isSelected = companyRole === option;

                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        setCompanyRole(option);
                        if (option !== 'Other') {
                          setCustomCompanyRole('');
                        }
                      }}
                      className={cn(
                        'cursor-pointer rounded-xl border px-3.5 py-2.5 text-sm transition-colors',
                        isSelected
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-border bg-background hover:border-foreground/40',
                      )}
                    >
                      {option === 'Other'
                        ? t('auth.signupFlow.roleOtherEllipsis')
                        : t(COMPANY_ROLE_LABEL_KEY[option])}
                    </button>
                  );
                })}
              </div>

              {companyRole === 'Other' ? (
                <div className="space-y-2">
                  <Label htmlFor="customCompanyRole">
                    {t('auth.signupFlow.customRoleLabel')}
                  </Label>
                  <Input
                    id="customCompanyRole"
                    value={customCompanyRole}
                    onChange={(event) =>
                      setCustomCompanyRole(event.target.value)
                    }
                    placeholder={t('common.optional')}
                  />
                </div>
              ) : null}
            </div>

            <Button
              type="button"
              className="h-11"
              onClick={handleAboutContinue}
              disabled={isSubmitting || !canContinueAbout}
            >
              {isSubmitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : null}
              {t('auth.signupFlow.continue')}
            </Button>
          </div>
        ) : null}

        {step === 'plan' ? (
          <div className="flex w-full flex-col gap-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold tracking-tight">
                {t('auth.signupFlow.planTitle')}
              </h2>
              <p className="text-pretty text-[15px] leading-relaxed text-muted-foreground">
                {t('auth.signupFlow.planSubtitle')}
              </p>
            </div>

            <div className="grid gap-3">
              {SIGNUP_PLAN_CARD_OPTIONS.map((option) => {
                const isSelected = planId === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setPlanId(option.id)}
                    className={cn(
                      'flex cursor-pointer flex-col gap-4 rounded-xl border p-4 text-left transition-all sm:p-5',
                      isSelected
                        ? 'border-foreground bg-foreground text-background shadow-md ring-1 ring-foreground/20'
                        : 'border-border bg-card shadow-sm hover:border-muted-foreground/40 hover:shadow-md dark:bg-card/80',
                    )}
                  >
                    <div className="space-y-1">
                      <div className="text-base font-semibold leading-tight">
                        {t(option.titleKey)}
                      </div>
                      <p
                        className={cn(
                          'text-[15px] leading-snug',
                          isSelected
                            ? 'text-background/85'
                            : 'text-muted-foreground',
                        )}
                      >
                        {t(option.taglineKey)}
                      </p>
                      <p
                        className={cn(
                          'pt-1 text-xl font-bold tabular-nums tracking-tight sm:text-2xl',
                          isSelected ? 'text-background' : 'text-foreground',
                        )}
                      >
                        {t(option.priceKey)}
                      </p>
                      <p
                        className={cn(
                          'text-sm',
                          isSelected
                            ? 'text-background/80'
                            : 'text-muted-foreground',
                        )}
                      >
                        {t(option.priceDetailKey)}
                      </p>
                    </div>
                    <ul
                      className={cn(
                        'space-y-2.5 border-t pt-4',
                        isSelected
                          ? 'border-background/20'
                          : 'border-border/70',
                      )}
                    >
                      {option.featureKeys.map((featureKey) => (
                        <li
                          key={featureKey}
                          className="flex gap-2.5 text-sm leading-snug"
                        >
                          <Check
                            className={cn(
                              'mt-0.5 size-4 shrink-0',
                              isSelected
                                ? 'text-background/90'
                                : 'text-primary',
                            )}
                            aria-hidden
                          />
                          <span
                            className={cn(
                              isSelected
                                ? 'text-background/95'
                                : 'text-foreground/90',
                            )}
                          >
                            {t(featureKey)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            <Button
              type="button"
              size="lg"
              className="h-12 w-full text-base font-semibold shadow-sm"
              onClick={handlePlanContinue}
              disabled={isSubmitting || !planId}
            >
              {isSubmitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : null}
              {t('auth.signupFlow.continue')}
            </Button>
          </div>
        ) : null}

        {step === 'enterprise' ? (
          <div className="flex flex-col gap-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold">
                {t('auth.signupFlow.enterpriseTitle')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('auth.signupFlow.enterpriseDescription')}
              </p>
            </div>

            <Button
              type="button"
              className="h-11"
              onClick={handleEnterpriseContact}
            >
              <Calendar className="size-4" />
              {t('auth.signupFlow.enterpriseBookMeeting')}
            </Button>

            <Button type="button" variant="ghost" onClick={handleBack}>
              {t('common.back')}
            </Button>
          </div>
        ) : null}
      </div>
    </AuthPageWrapper>
  );
}
